// Product Tutorial — 4 coach-marks that guide the first-time user to key
// features AFTER the onboarding cards close. Uses `getBoundingClientRect()`
// on the target testid to draw a spotlight cutout + tooltip. Skippable at
// any time and marks `localStorage.dwj_tutorial_v1` when finished/skipped.
import React, { useEffect, useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, ArrowRight, Sparkles } from "lucide-react";
import { useLang } from "../lib/i18n";

const STORAGE_KEY = "dwj_tutorial_v1";

const T = {
    en: {
        skip: "Skip tour",
        next: "Next",
        done: "Got it",
        steps: [
            { title: "Search anywhere in USA & Canada", body: "Type a city, ZIP code or job title. Suggestions appear as you type." },
            { title: "Filter by job category", body: "Tap any category chip to narrow the map to just those jobs — Restaurant, Warehouse, Delivery and more." },
            { title: "Set up daily job alerts", body: "Get an email every morning with fresh jobs matching your filters. No signup needed to browse first." },
            { title: "Tap any pin to apply", body: "Every pin is a real hiring employer. One tap opens their real application page — no resume walls." },
        ],
    },
    es: {
        skip: "Omitir tour",
        next: "Siguiente",
        done: "Entendido",
        steps: [
            { title: "Busca en cualquier parte de EE. UU. y Canadá", body: "Escribe una ciudad, código postal o puesto. Las sugerencias aparecen al escribir." },
            { title: "Filtra por categoría", body: "Toca un chip de categoría para ver solo esos empleos — Restaurante, Almacén, Reparto y más." },
            { title: "Configura alertas diarias", body: "Recibe un correo cada mañana con nuevos empleos según tus filtros. No hace falta registrarse para explorar." },
            { title: "Toca un pin para postular", body: "Cada pin es un empleador real. Un toque abre su página de postulación — sin muros de currículum." },
        ],
    },
};

const STEPS = [
    { testId: "search-input", placement: "bottom" },
    { testId: "filter-chips", placement: "bottom" },
    { testId: "site-alerts-btn", placement: "left" },
    { testId: "map-view", placement: "top", centered: true },
];

export function hasSeenTutorial() {
    try { return !!localStorage.getItem(STORAGE_KEY); } catch { return true; }
}
export function markTutorialSeen() {
    try { localStorage.setItem(STORAGE_KEY, "1"); } catch { /* ignore */ }
}

function getRect(testId) {
    const el = document.querySelector(`[data-testid="${testId}"]`);
    if (!el) return null;
    const r = el.getBoundingClientRect();
    return { x: r.left, y: r.top, w: r.width, h: r.height };
}

export default function ProductTutorial({ onFinish }) {
    const { lang } = useLang();
    const dict = T[lang] || T.en;
    const [step, setStep] = useState(0);
    const [rect, setRect] = useState(null);
    const [vw, setVw] = useState(() => window.innerWidth);
    const [vh, setVh] = useState(() => window.innerHeight);
    const current = STEPS[step];
    const stepCopy = dict.steps[step];
    const isMobile = vw < 768;

    const close = (skipped = false) => {
        markTutorialSeen();
        onFinish && onFinish({ skipped, atStep: step });
    };

    // Measure the target element on step change + window resize.
    useEffect(() => {
        let raf;
        const measure = () => {
            const r = getRect(current.testId);
            setRect(r);
        };
        // Slight delay so the DOM has settled (e.g. header re-layout).
        raf = requestAnimationFrame(() => {
            measure();
            // Re-measure a moment later in case fonts / images shift things.
            setTimeout(measure, 250);
        });
        const onResize = () => {
            setVw(window.innerWidth);
            setVh(window.innerHeight);
            measure();
        };
        window.addEventListener("resize", onResize);
        return () => {
            cancelAnimationFrame(raf);
            window.removeEventListener("resize", onResize);
        };
    }, [step, current.testId]);

    // Fallback rect (centred) when target isn't yet on screen.
    const safeRect = rect || {
        x: vw / 2 - 100, y: vh / 2 - 40, w: 200, h: 80,
    };

    // Spotlight geometry with padding.
    const PAD = 12;
    const sx = Math.max(0, safeRect.x - PAD);
    const sy = Math.max(0, safeRect.y - PAD);
    const sw = safeRect.w + PAD * 2;
    const sh = safeRect.h + PAD * 2;

    // Tooltip position (basic placement).
    const tooltip = useMemo(() => {
        const width = isMobile ? Math.min(vw - 24, 340) : 360;
        let top, left, arrow = "top";
        if (current.centered || !rect) {
            top = vh / 2 - 90;
            left = vw / 2 - width / 2;
            arrow = "none";
        } else if (current.placement === "bottom") {
            top = sy + sh + 14;
            left = Math.min(Math.max(12, sx + sw / 2 - width / 2), vw - width - 12);
            arrow = "top";
        } else if (current.placement === "left") {
            top = sy;
            left = Math.max(12, sx - width - 14);
            arrow = "right";
            // Mobile: fall back to bottom to avoid overflowing off-screen.
            if (isMobile || left < 12) {
                top = sy + sh + 14;
                left = Math.min(Math.max(12, sx + sw / 2 - width / 2), vw - width - 12);
                arrow = "top";
            }
        } else {
            top = sy - 180;
            left = Math.min(Math.max(12, sx + sw / 2 - width / 2), vw - width - 12);
            arrow = "bottom";
            if (top < 12) {
                top = sy + sh + 14;
                arrow = "top";
            }
        }
        return { top, left, width, arrow };
    }, [current, sx, sy, sw, sh, vw, vh, isMobile, rect]);

    const isLast = step === STEPS.length - 1;

    return (
        <AnimatePresence>
            <motion.div
                key="tutorial-root"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.2 }}
                className="fixed inset-0 z-[450]"
                data-testid="product-tutorial"
                aria-live="polite"
            >
                {/* Dim overlay with an SVG-based spotlight cutout for the target. */}
                <svg
                    width={vw}
                    height={vh}
                    className="absolute inset-0 pointer-events-none"
                    style={{ filter: "drop-shadow(0 0 20px rgba(232,88,18,0.35))" }}
                >
                    <defs>
                        <mask id="dwj-tut-mask">
                            <rect width={vw} height={vh} fill="white" />
                            <rect
                                x={sx}
                                y={sy}
                                width={sw}
                                height={sh}
                                rx={16}
                                ry={16}
                                fill="black"
                            />
                        </mask>
                    </defs>
                    <rect
                        width={vw}
                        height={vh}
                        fill="rgba(15,18,30,0.68)"
                        mask="url(#dwj-tut-mask)"
                    />
                    {/* Bright ring around the target to draw the eye. */}
                    <rect
                        x={sx}
                        y={sy}
                        width={sw}
                        height={sh}
                        rx={16}
                        ry={16}
                        fill="none"
                        stroke="#e85812"
                        strokeWidth="2.5"
                        opacity="0.9"
                    >
                        <animate attributeName="stroke-opacity" values="0.4;1;0.4" dur="2s" repeatCount="indefinite" />
                    </rect>
                </svg>

                {/* Click-catcher: tapping outside the tooltip advances the step. */}
                <button
                    type="button"
                    aria-label="advance tutorial"
                    className="absolute inset-0 w-full h-full cursor-pointer"
                    style={{ background: "transparent" }}
                    onClick={() => (isLast ? close(false) : setStep((s) => s + 1))}
                    data-testid="tutorial-overlay-catcher"
                />

                {/* Tooltip */}
                <motion.div
                    key={`tooltip-${step}`}
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.22, ease: [0.2, 0.8, 0.2, 1] }}
                    style={{
                        position: "absolute",
                        top: tooltip.top,
                        left: tooltip.left,
                        width: tooltip.width,
                    }}
                    className="bg-white rounded-2xl shadow-2xl p-4 md:p-5"
                    data-testid={`tutorial-step-${step + 1}`}
                    onClick={(e) => e.stopPropagation()}
                >
                    <div className="flex items-start gap-3">
                        <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-[#ffdccb] to-[#ffb488] flex items-center justify-center shrink-0">
                            <Sparkles className="w-4 h-4 text-[#e85812]" />
                        </div>
                        <div className="flex-1 min-w-0">
                            <div className="text-[10.5px] font-bold uppercase tracking-[0.14em] text-[#e85812]">
                                {step + 1} / {STEPS.length}
                            </div>
                            <h3 className="mt-1 font-heading font-extrabold text-[16px] md:text-[17px] leading-tight text-[#1a1e2c]">
                                {stepCopy.title}
                            </h3>
                            <p className="mt-1.5 text-[13px] leading-relaxed text-[#4a536f]">
                                {stepCopy.body}
                            </p>
                        </div>
                        <button
                            type="button"
                            onClick={() => close(true)}
                            className="p-1 rounded-full hover:bg-slate-100 text-slate-400"
                            aria-label={dict.skip}
                            data-testid="tutorial-close-btn"
                        >
                            <X className="w-4 h-4" />
                        </button>
                    </div>
                    <div className="mt-3 flex items-center justify-between gap-3">
                        <button
                            type="button"
                            onClick={() => close(true)}
                            className="text-[12.5px] font-medium text-slate-500 hover:text-[#e85812] transition-colors"
                            data-testid="tutorial-skip-btn"
                        >
                            {dict.skip}
                        </button>
                        <button
                            type="button"
                            onClick={() => (isLast ? close(false) : setStep((s) => s + 1))}
                            className="inline-flex items-center gap-1.5 bg-[#e85812] hover:bg-[#d94b08] text-white font-heading font-semibold text-[13.5px] px-4 py-2 rounded-full shadow-md hover:shadow-lg hover:-translate-y-0.5 transition-all"
                            data-testid={isLast ? "tutorial-finish-btn" : "tutorial-next-btn"}
                        >
                            {isLast ? dict.done : dict.next}
                            <ArrowRight className="w-3.5 h-3.5" />
                        </button>
                    </div>
                </motion.div>
            </motion.div>
        </AnimatePresence>
    );
}
